import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Calendar, Users, TrendingUp, ChartBar } from 'lucide-react';
import { API_BASE_URL } from '../config/environment';

// StatsCard Component
const StatsCard = memo(({ title, value, subValue, icon: Icon, variant }) => {
  const variants = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    purple: 'bg-purple-50 text-purple-700'
  };

  return (
    <div className={`p-4 rounded-lg shadow-sm ${variants[variant]} transition-transform hover:scale-102`}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold opacity-90">{title}</h3>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {subValue && (
            <p className="text-sm mt-1 opacity-75">Unikalne: {subValue}</p>
          )}
        </div>
        {Icon && <Icon className="opacity-50" size={24} />}
      </div>
    </div>
  );
});

StatsCard.displayName = 'StatsCard';

// ViewToggle Component
const ViewToggle = memo(({ viewMode, onViewChange }) => {
  return (
    <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
      <button
        onClick={() => onViewChange('daily')}
        className={`px-4 py-2 rounded-md transition-colors ${
          viewMode === 'daily' 
            ? 'bg-white text-blue-600 shadow-sm' 
            : 'text-gray-600 hover:bg-gray-200'
        }`}
      >
        Dzienne
      </button>
      <button
        onClick={() => onViewChange('monthly')}
        className={`px-4 py-2 rounded-md transition-colors ${
          viewMode === 'monthly' 
            ? 'bg-white text-blue-600 shadow-sm' 
            : 'text-gray-600 hover:bg-gray-200'
        }`}
      >
        Miesięczne
      </button>
    </div>
  );
});

ViewToggle.displayName = 'ViewToggle';

// ChartContainer Component
const ChartContainer = memo(({ title, children }) => {
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <ChartBar size={20} className="text-gray-400" />
        {title}
      </h3>
      <div className="h-72 md:h-96 w-full">
        {children}
      </div>
    </div>
  );
});

ChartContainer.displayName = 'ChartContainer';

// Główny komponent Statistics
const Statistics = () => {
  const [stats, setStats] = useState({
    today: { total: 0, unique: 0 },
    total: { total: 0, unique: 0 },
    pages: []
  });
  const [dailyStats, setDailyStats] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [viewMode, setViewMode] = useState('daily');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pobieranie danych
  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [statsResponse, dailyResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/admin/stats/daily`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!statsResponse.ok || !dailyResponse.ok) {
        throw new Error('Problem z pobraniem danych');
      }

      const [statsData, dailyData] = await Promise.all([
        statsResponse.json(),
        dailyResponse.json()
      ]);

      // Formatowanie danych miesięcznych
      const monthlyData = dailyData.reduce((acc, day) => {
        const month = day.date.substring(0, 7);
        if (!acc[month]) {
          acc[month] = {
            date: month,
            visits: 0,
            unique_visits: 0
          };
        }
        acc[month].visits += day.visits;
        acc[month].unique_visits += day.unique_visits;
        return acc;
      }, {});

      setStats(statsData);
      setDailyStats(dailyData);
      setMonthlyStats(Object.values(monthlyData));
      setError(null);
    } catch (err) {
      console.error('Błąd podczas pobierania statystyk:', err);
      setError('Nie udało się pobrać statystyk');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-center">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Karty statystyk */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatsCard
          title="Dzisiejsze wizyty"
          value={stats.today?.total || 0}
          subValue={stats.today?.unique || 0}
          icon={Calendar}
          variant="blue"
        />
        <StatsCard
          title="Wszystkie wizyty"
          value={stats.total?.total || 0}
          subValue={stats.total?.unique || 0}
          icon={TrendingUp}
          variant="green"
        />
        <StatsCard
          title="Unikalne wizyty (dziś)"
          value={stats.today?.unique || 0}
          icon={Users}
          variant="purple"
        />
      </div>

      {/* Przełącznik widoku i wykres wizyt */}
      <div className="space-y-4">
        <ViewToggle viewMode={viewMode} onViewChange={setViewMode} />
        
        <ChartContainer title={viewMode === 'daily' ? 'Wizyty dzienne' : 'Wizyty miesięczne'}>
          <ResponsiveContainer>
            <LineChart
              data={viewMode === 'daily' ? dailyStats : monthlyStats}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }} 
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="visits" 
                name="Wszystkie wizyty"
                stroke="#2563eb" 
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="unique_visits" 
                name="Unikalne wizyty"
                stroke="#7c3aed" 
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>

        {/* Statystyki stron */}
        <ChartContainer title="Najczęściej odwiedzane strony">
          <ResponsiveContainer>
            <BarChart
              data={stats.pages}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              barSize={20}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="page_url" 
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={100}
                interval={0}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }} 
              />
              <Legend />
              <Bar 
                dataKey="visits" 
                name="Wszystkie wizyty" 
                fill="#2563eb" 
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="unique_visits" 
                name="Unikalne wizyty" 
                fill="#7c3aed" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </div>
    </div>
  );
};

export default Statistics;